  import axios from "axios";
  import Swal from "sweetalert2";
  import DOMPurify from "dompurify";
  import React, { useEffect, useState, useRef } from "react";
  const DynamicForm = ({ formularioId, persona: personaInicial }) => {
    const BASE_URL = process.env.REACT_APP_BASEURL;
    const firstInputRef = useRef(null);
    const [formulario, setFormulario] = useState(null);
    const [respuestas, setRespuestas] = useState({});
    const [originalRespuestas, setOriginalRespuestas] = useState({});
    const [repetibles, setRepetibles] = useState({});
    const [originalRepetibles, setOriginalRepetibles] = useState({});
    const [seccionActual, setSeccionActual] = useState(0);
    const [modoPreview, setModoPreview] = useState(false);
    const [persona, setPersona] = useState(personaInicial || null);
    const [postulacionId, setPostulacionId] = useState(null);
    const [errores, setErrores] = useState({});
    const [animando, setAnimando] = useState(false);
    const [direccion, setDireccion] = useState("forward");

    // -------------------------
    // NORMALIZAR VALORES: quita comillas inicial/final
    // -------------------------
    const styles = {
      container: {
        maxWidth: 1000,
        margin: "0 auto",
        padding: 20,
        fontFamily: "Inter, Arial",
        background: "#f5f7fb"
      },
      card: {
        background: "#fff",
        padding: 20,
        borderRadius: 12,
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
        marginBottom: 20
      },
      input: {
        width: "100%",
        padding: 10,
        borderRadius: 8,
        border: "1px solid #ccc",
        marginTop: 5,
        marginBottom: 10
      },
      button: {
        padding: "8px 14px",
        borderRadius: 8,
        border: "none",
        cursor: "pointer",
        fontWeight: "bold"
      },
      primary: {
        background: "#4caf50",
        color: "#fff"
      },
      secondary: {
        background: "#2196f3",
        color: "#fff"
      },
      danger: {
        background: "#f44336",
        color: "#fff"
      },
      chip: {
        padding: "6px 10px",
        borderRadius: 20,
        background: "#eee",
        cursor: "pointer",
        fontWeight: "bold"
      },
      chipActive: {
        background: "#4caf50",
        color: "#fff"
      },
      animContainer: {
        transition: "all 0.25s ease",
      },
      slideInForward: {
        transform: "translateX(0)",
        opacity: 1
      },
      slideOutForward: {
        transform: "translateX(-40px)",
        opacity: 0
      },
      slideInBack: {
        transform: "translateX(0)",
        opacity: 1
      },
      slideOutBack: {
        transform: "translateX(40px)",
        opacity: 0
      },
    };
    const limpiarValor = (val) => {
      if (typeof val === "string") return val.replace(/^"(.*)"$/, "$1");
      return val;
    };

    // -------------------------
    // NORMALIZAR REPETIBLES
    // -------------------------
    const normalizarRepetibles = (rep) => {
      const salida = {};
      for (const campoId in rep) {
        salida[campoId] = (rep[campoId] || []).map(item => ({
          ...item,
          DNI: item.DNI || "",
          nombre_y_apellido: item.nombre_y_apellido || "",
          edad: item.edad || 0,
          genero: item.genero || "",
          convive: !!item.convive,
          tipo: item.tipo || "Hijo", // Nuevo campo para diferenciar tipo de persona
          escuela: item.escuela || "",
          empresa: item.empresa || "",
          puesto: item.puesto || "",
          desde: item.desde || "",
          hasta: item.hasta || ""
        }));
      }
      return salida;
    };

    // -------------------------
    // CARGAR FORMULARIO Y POSTULACION
    // -------------------------
    useEffect(() => {
      cargarFormulario();
    }, []);
    useEffect(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, [seccionActual, modoPreview]);
    const obtenerIdRepetible = (modelo) => {
      for (let sec of formulario.Seccions) {
        for (let c of sec.Campos) {
          if (c.tipo === "repetible" && c.modelo === modelo) return c.id;
        }
      }
      return null;
    };
    useEffect(() => {
      setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 250); // espera a que termine la animación
    }, [seccionActual, modoPreview]);

    useEffect(() => {
      if (!formulario || !personaInicial) return;

      const cargarPostulacion = async () => {
        try {
          const resPost = await axios.post(`${BASE_URL}ingresos/obtenerPostulacionPorDNI`, {
            dni: personaInicial.dni,
            formulario_id: formularioId
          });

          if (!resPost.data?.Postulacion) return;

          const post = resPost.data.Postulacion;

          // -------------------------
          // 1️⃣ Mapear respuestas
          // -------------------------
          const mapRespuestas = {};
          (post.Respuestas || []).forEach(r => {
            if (r.Campo?.clave) mapRespuestas[r.Campo.clave] = limpiarValor(r.valor);
          });
          setRespuestas(mapRespuestas);
          setOriginalRespuestas(JSON.parse(JSON.stringify(mapRespuestas)));

          // -------------------------
          // 2️⃣ Mapear repetibles
          // -------------------------
          const rep = {};
          const idHijos = obtenerIdRepetible("persona_hijos");
          const idExp = obtenerIdRepetible("persona_experiencia");

          if (resPost.data.PersonaHijos && idHijos) {
            rep[idHijos] = resPost.data.PersonaHijos.map(h => ({
              DNI: limpiarValor(h.DNI || ""),
              nombre_y_apellido: limpiarValor(h.nombre_y_apellido || ""),
              edad: h.edad || "",
              escuela: limpiarValor(h.escuela || ""),
              convive: !!h.convive,
              genero: h.genero || "",
              tipo: h.tipo || "Hijo", // fallback
            }));
          }

          if (resPost.data.PersonaExperiencia && idExp) {
            rep[idExp] = resPost.data.PersonaExperiencia.map(e => ({
              empresa: limpiarValor(e.empresa || ""),
              puesto: limpiarValor(e.puesto || ""),
              desde: formatDate(e.desde),
              hasta: formatDate(e.hasta),
            }));
          }

          // -------------------------
          // 3️⃣ Guardar en estado
          // -------------------------
          setRepetibles(rep);
          setOriginalRepetibles(JSON.parse(JSON.stringify(rep)));
          setPersona(prev => ({
            ...prev,                   // conserva los datos iniciales
            ...resPost.data,            // sobreescribe con lo que viene del backend
            dni: personaInicial.dni,   // asegura dni correcto
            nombre: personaInicial.nombre,
            apellido: personaInicial.apellido,
            celular_personal: personaInicial.celular_personal,
            fecha_nacimiento: personaInicial.fecha_nacimiento
          }));

        } catch (error) {
          console.warn("No se encontró postulacion existente:", error);
        }
      };

      cargarPostulacion();
    }, [formulario]);


    useEffect(() => {
      if (!formulario || !persona) return;

      const iniciales = { ...respuestas };

      formulario.Seccions
        .flatMap(sec => sec.Campos)
        .filter(c => c.origen === "persona")
        .forEach(campo => {
          if (iniciales[campo.clave] === undefined || iniciales[campo.clave] === "") {
            iniciales[campo.clave] = persona[campo.campo_db] || "";
          }
        });

      setRespuestas(iniciales);
    }, [formulario, persona]);
    const cargarFormulario = async () => {
      try {
        const res = await axios.post(`${BASE_URL}ingresos/formularios/${formularioId}`);
        setFormulario(res.data);

        if (personaInicial?.dni) {
          try {
            const resPost = await axios.post(`${BASE_URL}ingresos/obtenerPostulacionPorDNI`, {
              dni: personaInicial.dni,
              formulario_id: formularioId
            });
//             console.log(res)
            if (resPost.data?.Postulacion) {
              const post = resPost.data.Postulacion;
              setPostulacionId(post.id);

              // Mapear respuestas y limpiar comillas
              const mapRespuestas = {};
              (post.Respuestas || []).forEach(r => {
                if (r.Campo?.clave) mapRespuestas[r.Campo.clave] = limpiarValor(r.valor);
              });
              setRespuestas(mapRespuestas);
              setOriginalRespuestas(mapRespuestas);

              // Mapear repetibles
              const rep = {};
              const idHijos = obtenerIdRepetible("persona_hijos");
              const idExp = obtenerIdRepetible("persona_experiencia");

              if (resPost.data.PersonaHijos && idHijos)
                rep[idHijos] = resPost.data.PersonaHijos.map(h => ({
                  DNI: limpiarValor(h.DNI || ""),
                  nombre_y_apellido: limpiarValor(h.nombre_y_apellido || ""),
                  edad: h.edad || "",
                  escuela: limpiarValor(h.escuela || ""),
                  convive: !!h.convive,
                  genero: h.genero || "",
                  tipo: h.tipo || "Hijo" // Nuevo campo
                }));

              if (resPost.data.PersonaExperiencia && idExp)
                rep[idExp] = resPost.data.PersonaExperiencia.map(e => ({
                  empresa: limpiarValor(e.empresa || ""),
                  puesto: limpiarValor(e.puesto || ""),
                  desde: formatDate(e.desde),
                  hasta: formatDate(e.hasta)
                }));

              setRepetibles(rep);
              setOriginalRepetibles(rep);
              setPersona(resPost.data);
            }
          } catch (error) {
            console.warn("No se encontró postulacion existente:", error);
          }
        }
      } catch (error) {
        console.error("Error cargando formulario:", error);
      }
    };

    // -------------------------
    // MANEJO DE RESPUESTAS
    // -------------------------
    const handleChange = (campo, value) => {
      const limpio = Array.isArray(value)
        ? value
        : limpiarValor(value);

      setRespuestas(prev => ({
        ...prev,
        [campo.clave]: limpio
      }));
    };
    const formatDate = (date) => {
      if (!date) return "";
      return date.split("T")[0];
    };
    // -------------------------
    // REPETIBLES
    // -------------------------

    const agregarItem = (campo) => {
      const lista = repetibles[campo.id] || [];

      let tipo = "Hijo"; // default


      const nuevo = (() => {
        switch (campo.modelo) {
          case "persona_hijos":

            return {
              DNI: "",
              nombre_y_apellido: "",
              edad: "",
              genero: "",
              convive: false,
              tipo: "Otro"
            };
          case "persona_experiencia":
            return { empresa: "", puesto: "", desde: "", hasta: "" };
          default:
            return {};
        }
      })();

//       console.log("🟢 Campo modelo:", campo.modelo, "=> tipo asignado:", tipo, "objeto:", nuevo);

      setRepetibles({ ...repetibles, [campo.id]: [...lista, nuevo] });
    };
    const quitarItem = (campoId, index) => {
      const lista = [...(repetibles[campoId] || [])];
      lista.splice(index, 1);
      setRepetibles({ ...repetibles, [campoId]: lista });
    };

    const handleChangeRepetible = (campoId, index, key, value) => {
      const lista = [...(repetibles[campoId] || [])];
      lista[index][key] = limpiarValor(value);
      setRepetibles({ ...repetibles, [campoId]: lista });
    };

    const renderRepetible = (campo) => {
      const lista = repetibles[campo.id] || [];
      const seccion = formulario.Seccions[seccionActual];

      // ----------------------
      // Familia: separar por tipo
      // ----------------------
      if (campo.modelo === "persona_hijos") {
        const lista = repetibles[campo.id] || [];

        return (
          <div style={styles.card}>


            {/* Lista de hijos */}
            {lista.map((item, idx) => (
              <div key={idx} style={{ ...styles.card, marginBottom: 10 }}>
                <input
                  style={styles.input}
                  placeholder="DNI"
                  value={item.DNI}
                  onChange={e => handleChangeRepetible(campo.id, idx, "DNI", e.target.value)}
                />
                <input
                  style={styles.input}
                  placeholder="Nombre y apellido"
                  value={item.nombre_y_apellido}
                  onChange={e => handleChangeRepetible(campo.id, idx, "nombre_y_apellido", e.target.value)}
                />
                <input
                  style={styles.input}
                  type="number"
                  placeholder="Edad"
                  value={item.edad}
                  onChange={e => handleChangeRepetible(campo.id, idx, "edad", e.target.value)}
                />
                {/* 👇 ACA VA GENERO */}
                <select
                  value={item.genero}
                  style={styles.card}
                  onChange={e => handleChangeRepetible(campo.id, idx, "genero", e.target.value)}
                >
                  <option value="">Seleccione género</option>
                  <option value="Masculino">Masculino</option>
                  <option value="Femenino">Femenino</option>
                  <option value="Otro">Otro</option>
                </select>
                {/* Selector de tipo individual */}
                <select
                  value={item.tipo}
                  style={styles.card}
                  onChange={e => handleChangeRepetible(campo.id, idx, "tipo", e.target.value)}
                >
                  <option value="Hijo">Hijo</option>
                  <option value="Hermano">Hermano</option>
                  <option value="Pareja">Pareja</option>
                  <option value="Otro">Otro</option>
                </select>

                <label>
                  <input
                    type="checkbox"
                    style={styles.input}
                    checked={!!item.convive}
                    onChange={e => handleChangeRepetible(campo.id, idx, "convive", e.target.checked)}
                  />
                  Convive
                </label>

                <button
                  style={{ ...styles.button, ...styles.danger, marginTop: 10 }}
                  onClick={() => quitarItem(campo.id, idx)}
                >
                  Quitar
                </button>
              </div>
            ))}

            <button
              style={{ ...styles.button, ...styles.primary }}
              onClick={() => agregarItem(campo)}
            >
              ➕ Agregar familiar
            </button>
          </div>
        );
      }

      // ----------------------
      // Experiencia: mostrar cada objeto completo
      // ----------------------
      if (campo.modelo === "persona_experiencia") {
        return (
          <div style={styles.card}>
            <button
              style={{ ...styles.button, ...styles.primary }}
              onClick={() => agregarItem(campo)}
            >
              ➕ Agregar experiencia
            </button>

            {lista.map((item, idx) => (
              <div key={idx} style={{ ...styles.card, marginTop: 15 }}>
                <input style={styles.input} placeholder="Empresa"
                  value={item.empresa}
                  onChange={e => handleChangeRepetible(campo.id, idx, "empresa", e.target.value)} />

                <input style={styles.input} placeholder="Puesto"
                  value={item.puesto}
                  onChange={e => handleChangeRepetible(campo.id, idx, "puesto", e.target.value)} />

                <input style={styles.input} type="date"
                  value={item.desde}
                  onChange={e => handleChangeRepetible(campo.id, idx, "desde", e.target.value)} />

                <input style={styles.input} type="date"
                  value={item.hasta}
                  onChange={e => handleChangeRepetible(campo.id, idx, "hasta", e.target.value)} />

                <button
                  style={{ ...styles.button, ...styles.danger }}
                  onClick={() => quitarItem(campo.id, idx)}
                >
                  Quitar
                </button>
              </div>
            ))}
          </div>
        );
      }

      // ----------------------
      // Otros repetibles (si existieran)
      // ----------------------
      return (
        <div style={{ border: "1px dashed #888", padding: 10, borderRadius: 5, marginTop: 10 }}>
          <button onClick={() => agregarItem(campo)}>➕ Agregar</button>
          {lista.map((item, idx) => <div key={idx}>{JSON.stringify(item)}</div>)}
        </div>
      );
    };

    const renderCampo = (campo) => {
      if (campo.tipo === "repetible") return renderRepetible(campo);

      let value = respuestas[campo.clave] || "";
      if (campo.origen === "persona") value = persona?.[campo.campo_db] || "";

      if (typeof value === "string") value = limpiarValor(value);

      switch (campo.tipo) {

        case "text":

          return (
            <>
              <input
                ref={!firstInputRef.current ? firstInputRef : null}
                style={styles.input}
                value={value}
                onChange={(e) => handleChange(campo, e.target.value)}
              />
              {errores[campo.clave] && (
                <div style={{ color: "red", fontSize: 12 }}>
                  {errores[campo.clave]}
                </div>
              )}
            </>
          );
        case "number":
          return (<><input type="number" style={styles.input} value={value} onChange={(e) => handleChange(campo, e.target.value)} />{errores[campo.clave] && (
            <div style={{ color: "red", fontSize: 12 }}>
              {errores[campo.clave]}
            </div>
          )}</>)
 case "boolean":
  const opciones = (campo.CampoOpcions || [])
    .slice()
    .sort((a, b) => a.label.localeCompare(b.label));

  // aseguramos valor booleano
  const checked = value === true || value === "true" || value === "1";

  const labelNo = opciones[0]?.label || "No";
  const labelSi = opciones[1]?.label || "Sí";

  return (
    <div>
      <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span>{labelNo}</span>

        <div
          onClick={() => handleChange(campo, !checked)}
          style={{
            width: 50,
            height: 24,
            background: checked ? "#4caf50" : "#ccc",
            borderRadius: 20,
            position: "relative",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              width: 20,
              height: 20,
              background: "#fff",
              borderRadius: "50%",
              position: "absolute",
              top: 2,
              left: checked ? 28 : 2,
              transition: "0.3s",
            }}
          />
        </div>

        <span>{labelSi}</span>
      </label>

      {errores[campo.clave] && (
        <div style={{ color: "red", fontSize: 12 }}>
          {errores[campo.clave]}
        </div>
      )}
    </div>
  );
          case "date":
          return <><input type="date" style={styles.input} value={value} onChange={(e) => handleChange(campo, e.target.value)} />{errores[campo.clave] && (
            <div style={{ color: "red", fontSize: 12 }}>
              {errores[campo.clave]}
            </div>
          )}</>
        case "select":
          const opcionesOrdenadas = (campo.CampoOpcions || []).slice().sort((a, b) => a.label.localeCompare(b.label));
          return (<><select value={value} style={styles.input} onChange={(e) => handleChange(campo, e.target.value)}>
            <option value="">Seleccione</option>
            {opcionesOrdenadas.map(op => <option key={op.id} value={op.valor}>{op.label}</option>)}
          </select>{errores[campo.clave] && (
            <div style={{ color: "red", fontSize: 12 }}>
              {errores[campo.clave]}
            </div>
          )}</>

          );
        case "html_static":
          return (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(campo.contenido_html || ""),
              }}
            />
          );

        case "multi-select":
          const opcionesMulti = (campo.CampoOpcions || []).slice().sort((a, b) =>
            a.label.localeCompare(b.label)
          );

          // asegurarse de que value sea array
          let valueMulti = respuestas[campo.clave] || [];
          if (!Array.isArray(valueMulti)) valueMulti = [];

          return (
            <>
              <select
                style={styles.input}
                multiple
                value={valueMulti}
                onChange={(e) => {
                  const valoresSeleccionados = Array.from(e.target.selectedOptions, opt => opt.value);
                  handleChange(campo, valoresSeleccionados);
                }}
              >
                {opcionesMulti.map(op => (
                  <option key={op.id} value={op.valor}>
                    {op.label}
                  </option>
                ))}
              </select>
              {errores[campo.clave] && (
                <div style={{ color: "red", fontSize: 12 }}>
                  {errores[campo.clave]}
                </div>
              )}
            </>
          );
        case "textarea":
          return (
            <>
              <textarea
                style={{ ...styles.input, minHeight: 100, resize: "vertical" }}
                value={value}
                onChange={(e) => handleChange(campo, e.target.value)}
              />
              {errores[campo.clave] && (
                <div style={{ color: "red", fontSize: 12 }}>
                  {errores[campo.clave]}
                </div>
              )}
            </>
          );
        default:
          return null;
      }
    };



    const normalizar = (val) => {
      if (val === null || val === undefined) return "";
      if (Array.isArray(val)) return val.map(v => normalizar(v));
      if (typeof val === "boolean") return val ? "SI" : "NO";
      if (typeof val === "string") {
        const limpio = val.replace(/^"(.*)"$/, "$1").trim().toUpperCase();
        if (limpio === "TRUE") return "SI";
        if (limpio === "FALSE") return "NO";
        return limpio;
      }
      return String(val).toUpperCase();
    };

    // -------------------------
    // PARSE VALOR DEPENDENCIA
    // -------------------------
    function parseValorDependencia(valor) {
      if (!valor) return { tipo: "igual", valores: [] };

      if (typeof valor === "string") {
        try {
          return JSON.parse(valor);
        } catch {
          // string simple -> convertir a array
          return { tipo: "igual", valores: [valor] };
        }
      }

      // ya es objeto
      return valor;
    }

    // -------------------------
    // 🔥 MOSTRAR / OCULTAR CAMPO
    // -------------------------
  const mostrarCampo = (campo, seccionesOverride = null) => {
    let secciones = seccionesOverride || formulario?.Seccions || [];

    // 🔥 asegurar que sea array
    if (!Array.isArray(secciones)) {
      console.warn("⚠️ Secciones no es array:", secciones);
      secciones = Object.values(secciones); // fallback si viene como objeto
    }

    if (!campo.depende_de) return true;

    const campoPadre = secciones
      .flatMap(s => s.Campos || [])
      .find(c => String(c.id) === String(campo.depende_de));

    if (!campoPadre) return true;

    let valorPadre;

    if (campoPadre.origen === "persona") {
      valorPadre = persona?.[campoPadre.campo_db];
    } else {
      valorPadre = respuestas[campoPadre.clave];
    }

    const valorPadreNorm = normalizar(valorPadre);
    const depParsed = parseValorDependencia(campo.valor_dependencia);

    let coincide = false;

    if (depParsed.tipo === "igual") {
      coincide = depParsed.valores.some(v => normalizar(v) === valorPadreNorm);
    } else if (depParsed.tipo === "diferente") {
      coincide = !depParsed.valores.some(v => normalizar(v) === valorPadreNorm);
    } else {
      coincide = valorPadreNorm === normalizar(depParsed.valores[0]);
    }

    return coincide;
  };
    const validarTodo = () => {

      const nuevosErrores = {};
      let primeraSeccionConError = null;

      formulario.Seccions.forEach((sec, idx) => {

        sec.Campos.forEach((campo) => {

          if (!(campo.requerido === true || campo.requerido === 1 || campo.requerido === "1" || campo.requerido === "true")) return;

          // -------------------------
          // REPETIBLES
          // -------------------------
          if (campo.tipo === "repetible") {
            const lista = repetibles[campo.id] || [];

            if (!lista.length) {
              nuevosErrores[campo.id] = "Debe completar al menos un registro";
              if (primeraSeccionConError === null) primeraSeccionConError = idx;
              return;
            }

            const incompleto = lista.some(item =>
              Object.values(item).some(v => v === "" || v === null || v === undefined)
            );

            if (incompleto) {
              nuevosErrores[campo.id] = "Hay registros incompletos";
              if (primeraSeccionConError === null) primeraSeccionConError = idx;
            }

            return;
          }

          // -------------------------
          // CAMPOS NORMALES
          // -------------------------
          let valor;

          if (campo.origen === "persona") {
            valor = persona?.[campo.campo_db];
          } else {
            valor = respuestas[campo.clave];
          }
//           console.log("VALIDANDO", campo.label, campo.req);
          if (typeof valor === "string") valor = limpiarValor(valor);
          if (Array.isArray(valor) && valor.length === 0) {
            nuevosErrores[campo.clave] = "Campo requerido";
          }
          if (!valor || String(valor).trim() === "" || valor === "-") {
            nuevosErrores[campo.clave] = "Campo requerido";
            if (primeraSeccionConError === null) primeraSeccionConError = idx;
          }
        });
      });

      setErrores(nuevosErrores);

      return {
        valido: Object.keys(nuevosErrores).length === 0,
        primeraSeccionConError
      };
    };
    // -------------------------
    // GUARDAR PERSONA + POSTULACION
    // -------------------------
    const guardarPersonaYPostulacion = async () => {
      if (!persona) throw new Error("No hay datos de persona");

      const payload = {
        persona_id: persona.id || null,
        personaDatos: {
          dni: persona.dni,
          nombre: persona.nombre,
          apellido: persona.apellido,
          email: persona.email,
          celular_personal: persona.celular_personal,
          genero: persona.genero,
          fecha_nacimiento: persona.fecha_nacimiento,
        },
        formulario_id: formularioId,
        respuestas: Object.entries(respuestas).map(([clave, valor]) => {
          const campo = formulario.Seccions.flatMap(s => s.Campos).find(c => c.clave === clave);
          return { campo_id: campo?.id, valor };
        }),
        repetibles: normalizarRepetibles(repetibles),
      };

      try {
        Swal.fire({
          title: "Guardando postulacion...",
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });

        const resPostulacion = await axios.post(`${BASE_URL}ingresos/postulacion`, payload);

        Swal.close();

        setPostulacionId(resPostulacion.data.postulacion_id);

        // Si la persona no tenía id, lo actualizamos
        if (!persona.id && resPostulacion.data.persona_id) {
          setPersona({ ...persona, id: resPostulacion.data.persona_id });
        }

      } catch (error) {
        Swal.close();
        console.error("Error creando persona/postulación:", error);
        Swal.fire("Error", "No se pudo guardar la postulacion.", "error");
      }
    };
    // -------------------------
    // GUARDAR SECCION
    // -------------------------
    const validarDNIsUnicos = () => {
      const idHijos = obtenerIdRepetible("persona_hijos");
      if (!idHijos) return true;

      const listaHijos = repetibles[idHijos] || [];
      const dnis = listaHijos.map(h => h.DNI.trim()).filter(Boolean); // ignoramos vacíos
      const duplicados = dnis.filter((dni, idx) => dnis.indexOf(dni) !== idx);

      if (duplicados.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "DNI repetido",
          text: `Los siguientes DNI están repetidos: ${[...new Set(duplicados)].join(", ")}`,
        });
        return false;
      }

      return true;
    };
    const guardarSeccion = async (idx) => {
      if (!postulacionId) return;

      const seccion = formulario.Seccions[idx];

      // Solo respuestas de esta sección
      const respuestasSeccion = {};
      seccion.Campos.filter(c => c.tipo !== "repetible").forEach(c => {
        const nuevo = respuestas[c.clave] || persona?.[c.campo_db] || "";
        const original = originalRespuestas[c.clave] || persona?.[c.campo_db] || "";
        if (limpiarValor(nuevo) !== limpiarValor(original)) respuestasSeccion[c.id] = nuevo;
      });

      // Solo repetibles de esta sección
      const repetiblesSeccion = {};
      seccion.Campos.filter(c => c.tipo === "repetible").forEach(c => {
        const listaActual = repetibles[c.id] || [];
        const listaOriginal = originalRepetibles[c.id] || [];
        const listaNormalizada = normalizarRepetibles({ [c.id]: listaActual })[c.id];
        if (JSON.stringify(listaNormalizada) !== JSON.stringify(listaOriginal)) {
          // Manda incluso si está vacío
          repetiblesSeccion[c.id] = listaNormalizada;
        }
      });

      if (!Object.keys(respuestasSeccion).length && !Object.keys(repetiblesSeccion).length) return;

      const payload = {
        postulacion_id: postulacionId,
        respuestas: Object.entries(respuestasSeccion).map(([campo_id, valor]) => ({ campo_id, valor })),
        repetibles: repetiblesSeccion,
      };

      try {
        Swal.fire({
          title: `Guardando sección ${idx + 1}...`,
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });

        await axios.post(`${BASE_URL}ingresos/guardarSeccion`, payload);

        Swal.close();

        // Actualizamos los originales
        setOriginalRespuestas({ ...originalRespuestas, ...respuestasSeccion });
        setOriginalRepetibles({ ...originalRepetibles, ...repetiblesSeccion });

      } catch (error) {
        Swal.close();
        console.error("Error guardando sección:", error);
        Swal.fire("Error guardando sección", error.response?.data?.error || error.message, "error");
      }
    };


    const siguiente = async () => {
      try {
        setDireccion("forward");
        setAnimando(true);
        // 🔹 Validar DNIs únicos antes de guardar
        if (!validarDNIsUnicos()) {
          setAnimando(false);
          return; // 🚫 corta aquí si hay repetidos
        }
        setTimeout(async () => {
          // 🚨 Validar la primera sección antes de avanzar
          if (seccionActual === 0) {
            const primeraSec = formulario.Seccions[0];
            const erroresPrimera = {};

            primeraSec.Campos.forEach(campo => {
              if (!(campo.requerido === true || campo.requerido === 1 || campo.requerido === "1" || campo.requerido === "true")) return;

              let valor;

              if (campo.tipo === "repetible") {

                const lista = repetibles[campo.id] || [];
                if (!lista.length || lista.some(item => Object.values(item).some(v => v === "" || v === null || v === undefined))) {
                  erroresPrimera[campo.id] = "Debe completar los campos obligatorios";
                }
                return;
              } else if (campo.origen === "persona") {
                valor = persona?.[campo.campo_db];
              } else {
                valor = respuestas[campo.clave];
              }

              if (!valor || String(valor).trim() === "" || valor === "-") {
                erroresPrimera[campo.clave] = "Campo requerido";
              }
            });

            if (Object.keys(erroresPrimera).length > 0) {
              setErrores(erroresPrimera);
              Swal.fire("Completa los campos obligatorios", "Debes completar los campos requeridos antes de continuar", "warning");
              setAnimando(false);
              return; // 🚫 Corta aquí, no avanza
            }
          }

          // 🔹 Guardar persona / sección
          if (seccionActual === 0 && !postulacionId) await guardarPersonaYPostulacion();
          else if (postulacionId) await guardarSeccion(seccionActual);

          // 🔹 Avanzar de sección
          if (seccionActual < formulario.Seccions.length - 1) {
            setSeccionActual(seccionActual + 1);
          } else {
            await guardarSeccion(seccionActual);
            setModoPreview(true);
          }

          setAnimando(false);
        }, 200);

      } catch (error) {
        console.error(error);
        setAnimando(false);
      }
    };

    const anterior = () => {
      setDireccion("back");
      setAnimando(true);

      setTimeout(() => {
        if (modoPreview) setModoPreview(false);
        else if (seccionActual > 0) setSeccionActual(seccionActual - 1);

        setAnimando(false);
      }, 200);
    };

    const marcarPostulacionEnviada = async () => {
      if (!postulacionId) return;

      try {
        await axios.post(`${BASE_URL}ingresos/marcarPostulacionEnviada`, {
          postulacion_id: postulacionId
        });
        Swal.fire("Inscripción enviada ✅", "La postulación fue marcada como enviada.", "success");
      } catch (error) {
        console.error("Error marcando postulación como enviada:", error);
        Swal.fire("Error", "No se pudo marcar la postulación como enviada.", "error");
      }
    };

    const enviar = async () => {
      try {

        const { valido, primeraSeccionConError } = validarTodo();

        if (!valido) {
          Swal.fire("Inscripción incompleta", "Completá todos los campos obligatorios", "warning");

          if (primeraSeccionConError !== null) {
            setSeccionActual(primeraSeccionConError);
            setModoPreview(false);
          }

          return; // 🚫 CORTA ACÁ
        }

        // recién acá guardás
        if (!modoPreview && postulacionId) await guardarSeccion(seccionActual);

        await marcarPostulacionEnviada();
        setModoPreview(true);

      } catch (error) {
        console.error(error);
        Swal.fire("Error enviando formulario");
      }
    };

    // -------------------------
    // RENDER
    // -------------------------
    if (!formulario) return <p>Cargando...</p>;
    const seccion = formulario.Seccions[seccionActual];

    const renderNavSecciones = () => (
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        {formulario.Seccions.map((sec, idx) => (
          <div
            key={sec.id}
            onClick={() => { setSeccionActual(idx); setModoPreview(false); }}
            style={{
              ...styles.chip,
              ...(seccionActual === idx && !modoPreview ? styles.chipActive : {})
            }}
          >
            {sec.nombre}
          </div>
        ))}

        <div
          onClick={() => setModoPreview(true)}
          style={{
            ...styles.chip,
            ...(modoPreview ? styles.secondary : {})
          }}
        >
          Vista previa
        </div>
      </div>
    );

  const renderPreviewCampo = (campo) => {

    // 🔥 HTML STATIC
    if (campo.tipo === "html_static") {
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(campo.contenido_html || "ssS")
          }}
        />
      );
    }

    // 🔁 REPETIBLES
    if (campo.tipo === "repetible") {
      const lista = repetibles[campo.id] || [];
      if (!lista.length) return <em>No hay datos</em>;

      const keys = Object.keys(lista[0]);

      return (
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
          <thead>
            <tr>
              {keys.map(k => (
                <th key={k} style={{ border: "1px solid #ccc", padding: 5 }}>{k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lista.map((r, i) => (
              <tr key={i}>
                {keys.map(k => (
                  <td key={k} style={{ border: "1px solid #ccc", padding: 5 }}>
                    {String(r[k] || "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    // 🧾 CAMPOS NORMALES
    const valor = respuestas[campo.clave] || persona?.[campo.campo_db] || "-";

    return (
      <div style={{ padding: 5, border: "1px solid #ccc", borderRadius: 5, marginBottom: 5 }}>
        {valor}
      </div>
    );
  };
    return (
      <div style={{ maxWidth: 900, margin: "0 auto", padding: 20, fontFamily: "Arial, sans-serif" }}>
        <h2>{formulario.nombre}</h2>
        {renderNavSecciones()}

        <div
          style={{
            ...styles.animContainer,
            ...(animando
              ? direccion === "forward"
                ? styles.slideOutForward
                : styles.slideOutBack
              : direccion === "forward"
                ? styles.slideInForward
                : styles.slideInBack)
          }}
        >
          {modoPreview ? (
            <div>
              <h3>Vista previa antes de enviar</h3>
              {formulario.Seccions.map((sec, idx) => (
                <div
                  key={sec.id}
                  style={styles.card}
                  onClick={() => {
                    setSeccionActual(idx);
                    setModoPreview(false);
                  }}
                >
                  <h4>{sec.nombre}</h4>
                  {sec.Campos
    .filter(campo => campo.imprimible !== false) // 🔥 NUEVO
    .map(campo => (
                    <div key={campo.id}>
                      <strong>{campo.label}:</strong> {renderPreviewCampo(campo)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div>
              <h3>
                Sección {seccionActual + 1} de {formulario.Seccions.length}: {seccion.nombre}
              </h3>

              {seccion.Campos
                .filter(campo => campo.visible !== false) // 👈 ESTE
                .filter(mostrarCampo)
                .map(campo => (
                  <div key={campo.id} style={{ marginBottom: 15 }}>
                    <label style={{ fontWeight: "bold" }}>{campo.label}</label>
                    {renderCampo(campo)}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
          <button onClick={anterior} disabled={seccionActual === 0 && !modoPreview}>⬅️ Anterior</button>
          {modoPreview
            ? <button onClick={enviar}>Enviar ✅</button>
            : <button onClick={siguiente}>{seccionActual === formulario.Seccions.length - 1 ? "Vista previa ➡️" : "Siguiente ➡️"}</button>
          }
        </div>
      </div>
    );
  };

  export default DynamicForm;